// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Skeleton component for the inventory overview table while data is loading.
 * Provides a consistent loading experience that matches the structure of the actual table.
 */
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

export function InventoryOverviewSkeleton() {
  // Generate skeleton rows
  const skeletonRows = Array.from({ length: 8 }, (_, index) => (
    <TableRow key={index}>
      {/* Product name */}
      <TableCell>
        <Skeleton variant="text" width="120px" height={20} />
      </TableCell>

      {/* Location columns - simulate 3-4 location columns */}
      {Array.from({ length: 4 }, (_, colIndex) => (
        <TableCell key={colIndex} align="center">
          <Box display="flex" justifyContent="center" gap={1}>
            <Skeleton variant="text" width={24} height={16} />
            <Skeleton variant="text" width={24} height={16} />
            <Skeleton variant="text" width={24} height={16} />
          </Box>
        </TableCell>
      ))}

      {/* Total columns */}
      <TableCell align="center" sx={{ backgroundColor: 'grey.50' }}>
        <Box display="flex" justifyContent="center" gap={1}>
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
        </Box>
      </TableCell>

      {/* Non-inventory total */}
      <TableCell align="center" sx={{ backgroundColor: 'grey.100' }}>
        <Box display="flex" justifyContent="center" gap={1}>
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
        </Box>
      </TableCell>

      {/* In transit */}
      <TableCell align="center" sx={{ backgroundColor: 'grey.50' }}>
        <Box display="flex" justifyContent="center" gap={1}>
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
        </Box>
      </TableCell>

      {/* Grand total */}
      <TableCell align="center" sx={{ backgroundColor: 'primary.50' }}>
        <Box display="flex" justifyContent="center" gap={1}>
          <Skeleton variant="text" width={32} height={16} />
          <Skeleton variant="text" width={32} height={16} />
          <Skeleton variant="text" width={32} height={16} />
        </Box>
      </TableCell>
    </TableRow>
  ));

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Skeleton variant="text" width={60} height={20} />
            </TableCell>
            {Array.from({ length: 4 }, (_, index) => (
              <TableCell key={index} align="center">
                <Skeleton variant="text" width={80} height={20} />
              </TableCell>
            ))}
            <TableCell align="center" sx={{ backgroundColor: 'grey.50' }}>
              <Skeleton variant="text" width={100} height={20} />
            </TableCell>
            <TableCell align="center" sx={{ backgroundColor: 'grey.100' }}>
              <Skeleton variant="text" width={120} height={20} />
            </TableCell>
            <TableCell align="center" sx={{ backgroundColor: 'grey.50' }}>
              <Skeleton variant="text" width={80} height={20} />
            </TableCell>
            <TableCell align="center" sx={{ backgroundColor: 'primary.50' }}>
              <Skeleton variant="text" width={60} height={20} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{skeletonRows}</TableBody>
      </Table>
    </TableContainer>
  );
}
